/**
 * @fileoverview Wallet Scanner API Client (Orchard + UTXOs)
 *
 * Wraps the Winbit32 wallet-scanner service endpoints:
 *   - POST /wallet-scanner/orchard/export-ufvk
 *   - POST /wallet-scanner/orchard/scan-ufvk
 *   - POST /wallet-scanner/orchard/scan-ufvk/job
 *   - GET  /wallet-scanner/orchard/scan-ufvk/job/{jobId}
 *   - DELETE /wallet-scanner/orchard/scan-ufvk/job/{jobId}
 *   - POST /wallet-scanner/utxos
 *   - POST /wallet-scanner/transactions/broadcast
 *
 * Ported from WINBIT32 – uses shared constants for defaults.
 */

import {
	DEFAULT_WALLET_SCANNER_URL,
	BROADCAST_TIMEOUT_MS,
} from './constants';
import type {
	ScanJobStartResponse,
	ScanJobStatusResponse,
	UtxoResponse,
	BroadcastResponse,
} from './types';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface WalletScannerClientConfig {
	baseUrl?: string;
	apiKey?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Bypass any fetch interceptor the app may have installed.
 */
const getRawFetch = (): typeof fetch => {
	if (typeof window === 'undefined') return fetch;
	const w = window as any;
	return w.__WB32_FETCH_ORIGINAL__ || w.__WB32_NATIVE_FETCH__ || w.fetch || fetch;
};

async function handleJsonResponse<T>(res: Response): Promise<T> {
	let payload: any = null;
	try {
		payload = await res.json();
	} catch {
		throw new Error(`Invalid JSON response (status ${res.status})`);
	}
	if (!res.ok) {
		const msg = payload?.error || payload?.message || `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return payload as T;
}

// ---------------------------------------------------------------------------
// Client factory
// ---------------------------------------------------------------------------

export const createWalletScannerClient = (
	config: WalletScannerClientConfig = {},
) => {
	const baseUrl = (config.baseUrl || DEFAULT_WALLET_SCANNER_URL).replace(/\/$/, '');

	const withHeaders = (extra?: Record<string, string>) => {
		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
		};
		if (config.apiKey) headers['X-API-Key'] = config.apiKey;
		if (extra) Object.assign(headers, extra);
		return headers;
	};

	return {
		// ── UFVK export (prefer local derivation; this is a fallback) ──────
		exportUfvkRemote: async (body: {
			mnemonic: string;
			purpose?: number;
			coinType?: number;
			account?: number;
			change?: number;
			addressIndex?: number;
		}) => {
			const res = await fetch(
				`${baseUrl}/wallet-scanner/orchard/export-ufvk`,
				{
					method: 'POST',
					headers: withHeaders(),
					body: JSON.stringify(body),
				},
			);
			return handleJsonResponse<{
				success: boolean;
				data?: { ufvk: string };
				error?: string;
			}>(res);
		},

		// ── Immediate (synchronous) scan ───────────────────────────────────
		scanUfvkImmediate: async (body: {
			ufvk: string;
			birthdayHeight?: number;
		}) => {
			const res = await fetch(
				`${baseUrl}/wallet-scanner/orchard/scan-ufvk`,
				{
					method: 'POST',
					headers: withHeaders(),
					body: JSON.stringify(body),
				},
			);
			return handleJsonResponse<{
				success: boolean;
				data?: {
					notes: Array<{
						tx_hash: string;
						block_height: number;
						value: number;
						nullifier: string;
						commitment: string;
						ephemeral_key: string;
					}>;
				};
				error?: string;
			}>(res);
		},

		// ── Start async scan job ───────────────────────────────────────────
		// Non-blocking: the server returns { jobId, jobToken } immediately,
		// even with autoDetect:true (birthday detection runs in the
		// background, surfaced as progress.phase 'detecting-birthday').
		// Poll getOrchardScanJob until job.status === 'succeeded'.
		startOrchardScanJob: async (
			body: {
				ufvk: string;
				birthdayHeight?: number;
				endHeight?: number;
				witness?: boolean;
				autoDetect?: boolean;
				forceRescan?: boolean;
			},
			opts?: { apiKey?: string },
		): Promise<ScanJobStartResponse> => {
			const res = await fetch(
				`${baseUrl}/wallet-scanner/orchard/scan-ufvk/job`,
				{
					method: 'POST',
					headers: withHeaders(
						opts?.apiKey ? { 'X-API-Key': opts.apiKey } : undefined,
					),
					body: JSON.stringify(body),
				},
			);
			return handleJsonResponse<ScanJobStartResponse>(res);
		},

		// ── Poll job status ────────────────────────────────────────────────
		getOrchardScanJob: async (
			jobId: string,
			auth: { apiKey?: string; jobToken?: string },
		): Promise<ScanJobStatusResponse> => {
			const headers: Record<string, string> = withHeaders();
			if (auth.jobToken) headers['X-Job-Token'] = auth.jobToken;
			if (auth.apiKey) headers['X-API-Key'] = auth.apiKey;
			const res = await fetch(
				`${baseUrl}/wallet-scanner/orchard/scan-ufvk/job/${jobId}`,
				{ method: 'GET', headers },
			);
			return handleJsonResponse<ScanJobStatusResponse>(res);
		},

		// ── Cancel job ─────────────────────────────────────────────────────
		cancelOrchardScanJob: async (
			jobId: string,
			auth: { apiKey?: string; jobToken?: string },
		) => {
			const headers: Record<string, string> = withHeaders();
			if (auth.jobToken) headers['X-Job-Token'] = auth.jobToken;
			if (auth.apiKey) headers['X-API-Key'] = auth.apiKey;
			const res = await fetch(
				`${baseUrl}/wallet-scanner/orchard/scan-ufvk/job/${jobId}`,
				{ method: 'DELETE', headers },
			);
			return handleJsonResponse<{
				success: boolean;
				data?: { cancelled: boolean };
			}>(res);
		},

		// ── Transparent UTXOs ──────────────────────────────────────────────
		getUtxos: async (address: string): Promise<UtxoResponse> => {
			const res = await fetch(`${baseUrl}/wallet-scanner/utxos`, {
				method: 'POST',
				headers: withHeaders(),
				body: JSON.stringify({ address }),
			});
			return handleJsonResponse<UtxoResponse>(res);
		},

		// ── Broadcast raw tx hex ───────────────────────────────────────────
		broadcastRawTransaction: async (body: {
			rawTxHex: string;
			allowHighFees?: boolean;
		}): Promise<BroadcastResponse> => {
			const url = `${baseUrl}/wallet-scanner/transactions/broadcast`;
			const nativeFetch = getRawFetch();
			const controller = new AbortController();
			const timer = setTimeout(() => controller.abort(), BROADCAST_TIMEOUT_MS);

			const init: RequestInit = {
				method: 'POST',
				headers: withHeaders(),
				body: JSON.stringify(body),
				signal: controller.signal,
			};

			try {
				const res = await nativeFetch(url, init);
				return await handleJsonResponse<BroadcastResponse>(res);
			} catch (error: any) {
				if (error?.name === 'AbortError') {
					throw new Error(
						`Broadcast request timed out after ${BROADCAST_TIMEOUT_MS / 1000}s`,
					);
				}
				throw error;
			} finally {
				clearTimeout(timer);
			}
		},
	};
};

export type WalletScannerClientType = ReturnType<typeof createWalletScannerClient>;

